<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import GalleryIndicator from '$lib/galleryIndicator.svelte';
	import type { SbImageFormat } from '../types';
	import { carousels } from '$lib/state/storyblok/carousel.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<{
			carousel_id: string;
			assets: ISbStoryData[];
			aspect_ratio: Extract<SbImageFormat, '3/4' | '4/3' | '16/9'>;
			desktop_aspect_ratio: Extract<SbImageFormat, '3/4' | '4/3' | '16/9'>;
			gap: 'none' | 'small';
			peek: boolean;
			hide_desktop_indicators: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let embla = $state<EmblaCarouselType>();
	let active = $state(0);

	$effect(() => {
		if (embla && content.carousel_id) {
			carousels[content.carousel_id] = embla;
		}

		return () => {
			if (content.carousel_id) {
				delete carousels[content.carousel_id];
			}
		};
	});

	function next() {
		if (embla) {
			embla.scrollNext();
		}
	}

	function previous() {
		if (embla) {
			embla.scrollPrev();
		}
	}

	function getGapClass(gapSize: typeof content.gap) {
		switch (gapSize) {
			case 'small':
				return 'gap-x-5 mx-5';
			case 'none':
			default:
				return '';
		}
	}

	let gapClass = $derived(getGapClass(content.gap));
</script>

<div use:editable={content} class="relative">
	<Carousel bind:embla class="flex {gapClass}" id={content.carousel_id} bind:currentIndex={active}>
		{#each content.assets as image, index}
			{@const isActive = index === active}
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<div
				class:basis-full={!content.peek}
				class:basis-[90%]={content.peek}
				class:lg:basis-[70%]={content.peek}
				class:cursor-pointer={!isActive}
				class="flex-none aspect-{content.aspect_ratio ||
					'4/3'} lg:aspect-{content.desktop_aspect_ratio ||
					content.aspect_ratio ||
					'16/9'} overflow-hidden rounded-[20px]"
				onclick={() => {
					if (isActive) return;

					if (index > active) {
						next();
					} else {
						previous();
					}
				}}
			>
				<Blok content={image} />
			</div>
		{/each}
	</Carousel>

	{#if !content.hide_desktop_indicators && content.assets.length > 1}
		<button
			onclick={previous}
			class="absolute left-0 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-stone-5 p-4 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] disabled:opacity-25 max-lg:hidden"
			disabled={active === 0}
		>
			<ArrowLeft class="h-6 w-6" />
		</button>
		<button
			onclick={next}
			class="absolute right-0 top-1/2 flex -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-stone-5 p-4 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] disabled:opacity-25 max-lg:hidden"
			disabled={active >= content.assets.length - 1}
		>
			<ArrowRight class="h-6 w-6" />
		</button>
		<div class="mt-4 lg:absolute lg:inset-x-0 lg:bottom-6">
			<GalleryIndicator
				bind:active
				count={content.assets.length}
				onchange={(index) => embla?.scrollTo(index)}
			/>
		</div>
	{/if}
</div>
